<template>
  <component
    :is="wrapperComponent"
    class="sp-quantum-select"
    :class="classModifiers"
    :open="menuOpen"
    :close-on-content-click="closeOnContentClick"
    :pointer="pointer"
    :mobile-expand-height-max="mobileExpandHeightMax"
    :location="location"
    :permanent="permanent"
    @update-open="handleMenuUpdateOpen"
  >
    <div v-if="!noMenu" slot="activator" class="sp-quantum-select__activator">
      <slot name="activator">
        <sp-button>Open menu</sp-button>
      </slot>
    </div>
    <div class="sp-quantum-select__content">
      <NavigationContainerShell
        :max-height="computedMaxHeight"
        :appearance="props.appearance"
        :title="props.title"
        :hide-title-bar="props.hideTitleBar"
        :on-back="containerOnBack"
      >
        <template #before-content>
          <slot name="before-content" />
        </template>
        <component
          :is="containerComponent"
          :key="containerResetKey"
          :item-title="props.itemTitle"
          :item-value="props.itemValue"
          :item-subtitle="props.itemSubtitle"
          :item-tags="props.itemTags"
          :item-test-id="props.itemTestId"
          :input-test-id="props.inputTestId"
          :clearable="clearable"
          :listen-on-key-event="listenOnKeyEvent"
          :selected="model"
          :multiple="props.multiple"
          :required="props.required"
          :selected-prepend-icon="props.selectedPrependIcon"
          :selected-prepend-icon-fill-color="props.selectedPrependIconFillColor"
          :no-data-text="props.noDataText"
          :hide-no-data="props.hideNoData"
          :hide-search-bar="effectiveHideSearchBar"
          :no-filter="props.noFilter"
          :selectable-branches="props.selectableBranches"
          :branch-toggle-label="props.branchToggleLabel"
          :readonly="props.readonly"
          :loading="loading"
          :placeholder="placeholder"
          :wrap="props.wrap"
          :search="props.search"
          :items="props.items"
          :max-results="props.maxResults"
          :fuzzy-search="props.fuzzySearch"
          :highlight-first-result="props.highlightFirstResult"
          :load-children="props.loadChildren"
          :initial-path="props.initialPath"
          :accordion-caret-variant="props.accordionCaretVariant"
          @update-selected="handleUpdateSelected"
          @update-search="handleUpdateSearch"
          @update-current-item="handleUpdateCurrentItem"
          @select-item="handleSelectItem"
          @update:on-back="setContainerOnBack"
        />
      </NavigationContainerShell>
    </div>
  </component>
</template>

<script setup>
/**
 * QuantumSelect is an advanced web component designed to elevate the traditional select element into a highly
 * interactive and user-friendly interface.
 * It seamlessly integrates deep nested structure support, section headers, and a dynamic search input,
 * enabling users to effortlessly navigate and filter through complex datasets.
 *
 * @displayName QuantumSelect
 * @group Custom Elements
 * @component sp-quantum-select
 */
import { computed, ref, toRef, watch } from "vue";
import { isNoSelection, isPresent, toArray, toBoolean } from "#ce/utils/props";
import { useExpose } from "#ce/composables/useExpose";
import { useHostElement } from "#ce/composables/useHostElement";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useSpList } from "#ce/components/SpList/composables/useSpList";
import { useForm } from "#ce/composables/useForm";
import { useFormControl } from "#ce/composables/useFormControl";
import { t } from "#ce/shared/forms-i18n";
import { formAssociatedHost } from "#ce/framework/utils";
import { props as quantumSelectProps } from "./props.js";
import { useSelectionModel } from "./composables/useSelectionModel.js";
import AccordionContainer from "./components/AccordionContainer.ce.vue";
import DrilldownContainer from "./components/DrilldownContainer.ce.vue";
import FlatContainer from "./components/FlatContainer.ce.vue";
import NavigationContainerShell from "./components/NavigationContainerShell.ce.vue";

const emit = defineEmits([
  "update-selected",
  "update-search",
  "update-open",
  "update-current-item",
  "update-selected-item",
]);

const props = defineProps(quantumSelectProps);

// sp-quantum-select is a genuine form control: it submits its selection, so it can stand alone in
// a form rather than only inside a host that owns the value.
defineOptions(formAssociatedHost());

const menuOpen = ref(toBoolean(props.open));

// Bumped on every menu open when `startOnRoot` is set. Vue tears down the
// container element and rebuilds it with a fresh frame stack, so the host never
// sees this mechanism, only the prop.
const containerResetKey = ref(0);

// Back-button callback surfaced by the active navigation container via
// `update:on-back`; passed through to the shell.
const containerOnBack = ref();

function setContainerOnBack(value) {
  containerOnBack.value = value;
}

function clearContainerOnBack() {
  containerOnBack.value = undefined;
}

function syncMenuOpenFromProp(value) {
  menuOpen.value = toBoolean(value);
}

function handleMenuOpenChange(value) {
  emit("update-open", value);
  if (value && toBoolean(props.startOnRoot)) {
    containerResetKey.value++;
    emit("update-current-item", null);
  }
}

watch(() => props.open, syncMenuOpenFromProp);
watch(menuOpen, handleMenuOpenChange);

const listenOnKeyEvent = computed(() => toBoolean(props.listenOnKeyEvent) || menuOpen.value);

const noMenu = computed(() => toBoolean(props.noMenu));
const permanent = computed(() => toBoolean(props.permanent));
const wrapperComponent = computed(() => (noMenu.value ? "div" : "sp-menu"));

const { isGtSmScreen } = useBreakpoints();

// True exactly when the wrapper renders as a bottom sheet — mirrors SpMenu's
// own `showBottomSheet = !permanent && !isGtSmScreen` (only the sp-menu wrapper
// has a sheet, so `noMenu` is excluded too).
const inSheetMode = computed(() => !noMenu.value && !permanent.value && !isGtSmScreen.value);

// Effective search-bar visibility passed to the container. `hideSearchBar`
// normally hides it everywhere, but a host that hides it because a slotted
// activator doubles as the search input can opt back in for sheet mode via
// `showSearchBarInSheet`: there the activator is covered by the sheet, so the
// user needs the in-list search bar to filter.
const effectiveHideSearchBar = computed(() => {
  if (!toBoolean(props.hideSearchBar)) {
    return false;
  }
  return !(toBoolean(props.showSearchBarInSheet) && inSheetMode.value);
});

const containers = { drilldown: DrilldownContainer, flat: FlatContainer, accordion: AccordionContainer };
// Fall back to the drilldown container if `navigation` is an unsupported value:
// the prop validator only warns in dev, so without this `<component :is>` would
// receive `undefined` and render nothing in production.
const containerComponent = computed(() => containers[props.navigation] ?? DrilldownContainer);

// Switching navigation at runtime swaps the container; the new one is not
// required to emit `update:on-back`, so the previous callback would linger
// and leave a dead back button on the shell.
watch(containerComponent, clearContainerOnBack);
const computedMaxHeight = computed(() => {
  if (permanent.value && !props.maxHeight) {
    return "100%";
  }
  return props.maxHeight;
});

function handleMenuUpdateOpen({ detail }) {
  const [value] = detail;
  menuOpen.value = value;
}

const { getItemValue } = useSpList(props);

function closeMenuOnSelect() {
  if (toBoolean(props.closeOnSelect)) {
    menuOpen.value = false;
  }
}

const { model, selectByUser } = useSelectionModel(props, emit, { getItemValue, onChange: closeMenuOnSelect });

// The `selected` prop seeds the selection and names what a form reset restores; `value` is the
// live selection, in the shape `update-selected` carries.
const defaultSelected = toArray(props.selected);

// `toArray(undefined)` yields a one-entry array, so an unselected picker still has a member —
// the entries are what "selected" means here, not the array length.
const selectedValues = computed(() => model.value.map(getItemValue));
const selectedEntries = computed(() => selectedValues.value.filter((entry) => entry !== null && entry !== undefined));

const { exposeProperties } = useExpose();

exposeProperties({
  value: {
    configurable: true,
    get: () => selectedValues.value,
    set: (value) => {
      model.value = toArray(isNoSelection(value) ? [] : value);
    },
  },
  defaultValue: {
    configurable: true,
    get: () => defaultSelected,
  },
});

// The list lives in the shadow root, where the outer form cannot see it, so the element submits
// through ElementInternals — one entry per selection, the way a native <select multiple> does.
const { mirrorFormValue, mirrorValueMissing, onFormReset } = useForm();

const formValue = computed(() => {
  if (!props.name) {
    return null;
  }

  const data = new FormData();
  selectedEntries.value.forEach((entry) => data.append(props.name, String(entry)));

  return data;
});

mirrorFormValue(formValue);

onFormReset(() => (model.value = toArray(defaultSelected)));

// Disabled is governed by the form-control composable: presence-based own attribute plus ancestor
// <fieldset disabled>.
const { disabled } = useFormControl({ disabled: toRef(props, "disabled") });

const classModifiers = computed(() => ({ "--disabled": disabled.value }));

// A required picker is satisfied only once something is selected.
const isRequired = computed(() => isPresent(props.required));
const validationMessage = computed(() => props.requiredMessage || t("select_value_missing"));

mirrorValueMissing(() => isRequired.value && selectedEntries.value.length === 0, validationMessage);

function handleUpdateSelected(value) {
  // A disabled picker must not mutate form state, even through a container whose items stay
  // clickable.
  if (disabled.value) {
    return;
  }

  selectByUser(value);
}

function handleUpdateSearch(value) {
  emit("update-search", value);
}

function handleUpdateCurrentItem(item) {
  emit("update-current-item", item);
}

// A terminal (leaf) item was selected. Surface the complete item object so
// consumers can route on the full payload (e.g. url / filters / value) — the
// component itself stays agnostic of what those fields mean. `update-selected`
// (value only) continues to fire independently for the simple binding case.
function handleSelectItem(item) {
  emit("update-selected-item", item);
}

// Move roving focus to the open list's first / last option; no-op when the list
// isn't rendered (closed / no items).
const host = useHostElement();
const { exposeMethods } = useExpose();
exposeMethods({
  focusListItem(location) {
    host?.shadowRoot?.querySelector("sp-list")?.focusItem?.(location);
  },
  // The first selectable row the open list renders — read from the rendered
  // sp-list's items (same access path as focusListItem) — so an external
  // activator can commit it on Enter. The `highlight-first-result` marked row
  // is the contract (the hint and what Enter commits can never disagree); a
  // scan for the first selectable row is the fallback. Items are
  // panel-extended, so branch committability is already resolved into
  // `props.selectable`. Returns null when nothing is committable — the scan
  // stops at a primitive first candidate (no title/payload to commit) rather
  // than skipping to a row the hint would never have marked.
  firstResult() {
    const items = host?.shadowRoot?.querySelector("sp-list")?.items ?? [];
    const candidate = (item) =>
      item != null &&
      !item.header &&
      !toBoolean(item.disabled) &&
      !toBoolean(item.props?.disabled) &&
      item.props?.selectable !== false;
    const committable = (item) => candidate(item) && typeof item === "object";

    // Honor the marked row only when this picker's own highlight produced it
    // (the prop is on) and it is still committable — a consumer hand-marking
    // rows for visuals must not steer what Enter commits.
    if (toBoolean(props.highlightFirstResult)) {
      const marked = items.find((item) => item?.props?.highlighted);
      if (marked && committable(marked)) {
        return marked;
      }
    }

    const index = items.findIndex(candidate);
    const first = index === -1 ? null : items[index];
    return committable(first) ? first : null;
  },
});
</script>

<style>
:host {
  display: block;
  --quantum-select-content-padding: var(--sp-comp-quantum-select-content-padding, 0.75rem);
  --quantum-select-content-padding-top: var(--sp-comp-quantum-select-content-padding-top, 0.5rem);
  --quantum-select-content-padding-bottom: var(--sp-comp-quantum-select-content-padding-bottom, 0.5rem);
  --quantum-select-popup-min-height: var(
    --sp-comp-quantum-select-popup-min-height,
    var(--sp-comp-popup-min-height, 25rem)
  );
  --quantum-select-popup-max-height: var(
    --sp-comp-quantum-select-popup-max-height,
    var(--quantum-select-popup-min-height)
  );

  --default-select-popup-width: 18.5rem;
  --quantum-select-popup-width: var(--sp-comp-quantum-select-popup-width, var(--default-select-popup-width));
  --quantum-select-animation-time: var(--sp-comp-quantum-select-animation-time, 0.3s);

  --sp-comp-divider-color: var(--sp-comp-select-divider-color, #ccc);
}

:host([no-menu]) {
  --default-select-popup-width: 100%;
  height: 100%;
}

:host([permanent]) {
  height: 100%;
}

/*
 * Only the full-height inline modes (permanent / no-menu, e.g. the required
 * filters modal) should stretch the inner wrapper to fill the host. In the
 * default menu/activator mode the wrapper must size to its content so the
 * activator can be vertically centered by the parent layout.
 */
:host([permanent]) .sp-quantum-select,
:host([no-menu]) .sp-quantum-select {
  height: 100%;
}
</style>

<style lang="scss" scoped>
.sp-quantum-select {
  height: auto;
}

.sp-quantum-select__content {
  position: relative;
  height: 100%;
}
</style>
